/**
 * Modificación de Préstamos — migración web de ares2040.
 *
 * Busca un préstamo activo, carga todos sus datos (cliente, producto,
 * condiciones, fiador) y permite modificarlos. Si el préstamo NO tiene
 * pagos registrados se pueden recalcular las condiciones (nuevo precio,
 * cuotas, período e interés): el backend regenera la tabla de amortización
 * y audita el cambio en `modificaprestamos`. Con pagos registrados se pueden
 * editar los datos generales (igual que el escritorio) y, además, reprogramar
 * las fechas de las cuotas pendientes sin pago manteniendo las condiciones.
 * Con completivo del inicial pendiente se puede modificar su plan de fechas
 * (o crearlo si la venta no tenía): el plan nuevo reparte solo el pendiente.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, DocumentoField, fpInputSx } from "components/financepro";
import { documentoValido, tipoDocumento, formatDocumentoAuto } from "lib/documento";
import { useApi } from "lib/api";
import { calcularCuotas, useConfigCuotas } from "lib/calculocuotas";
import CampoCuota from "components/cuotas/CampoCuota";
import { PlanCompletivoEditor, PlanCompletivoResumen } from "components/completivos/PlanCompletivo";
import {
  estadoPlanCompletivo,
  validarPlanCompletivo,
  normalizarPlan,
  hoyISO,
} from "lib/completivoplan";

const FORM_INICIAL = {
  cedula: "",
  nombres: "",
  apellidos: "",
  direccion: "",
  provincia: "",
  telefono: "",
  celular: "",
  email: "",
  apodo: "",
  edad: "",
  chasis: "",
  marca: "",
  modelo: "",
  colore: "",
  garantia: "",
  comentario: "",
  fiador: "",
  fiadorced: "",
  fiadortel: "",
  fiadordir: "",
};

// excluirdomingos (solo Dias) y días fijos de quincena (solo Quincenas, ej.
// 15 y 30; vacíos = cada 15 días corridos) — mismas opciones que la venta.
// fechaprimeracuota reprograma desde qué fecha corre el nuevo calendario;
// vacía = parte de la fecha del préstamo, igual que el escritorio.
const NUEVO_INICIAL = {
  precio: "",
  cuotas: "",
  formadepago: "Meses",
  interes: "",
  excluirdomingos: false,
  diaquincena1: "",
  diaquincena2: "",
  fechaprimeracuota: "",
};

// Cuotas por mes de cada forma de pago (misma tabla que ares2010/_calcular).
const FACTOR_PERIODO = { Meses: 1, Quincenas: 2, Semanas: 4, Dias: 30 };

// Reprogramación de cuotas pendientes (préstamos con pagos): solo mueve las
// fechas de las cuotas 100% sin pago; condiciones y montos quedan iguales.
const REPROG_INICIAL = {
  fecha: "",
  excluirdomingos: false,
  diaquincena1: "",
  diaquincena2: "",
};

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// "Matricula RD$ 8,000.00 en 12 cuotas" para el aviso de cargos del recálculo.
const textoCargo = (c) => {
  const desc = c.descripcion ? ` (${c.descripcion})` : "";
  const n = c.cuotasnuevas || "—";
  return `${c.tipo}${desc} RD$ ${fmtMoney(c.valor)} en ${n} cuota${n === 1 ? "" : "s"}`;
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Punto de partida del editor del plan del completivo: lo vencido sin pagar
// se junta en un pago con fecha de hoy (el plan nuevo no admite fechas
// pasadas) y lo por vencer queda como estaba. Sin plan, todo el pendiente
// en un pago hoy.
const propuestaPlanCompletivo = (completivo) => {
  const pendiente = toNum(completivo?.pendiente);
  if (!(pendiente > 0)) return [];
  const hoy = hoyISO();
  const plan = completivo.plan || [];
  if (!plan.length) return [{ fecha: hoy, monto: pendiente }];
  const { partes, vencido } = estadoPlanCompletivo(plan, pendiente, hoy);
  const porVencer = partes
    .filter((x) => x.estado === "porvencer")
    .map((x) => ({ fecha: x.fecha, monto: x.pendiente }));
  return vencido > 0 ? [{ fecha: hoy, monto: vencido }, ...porVencer] : porVencer;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function ModificacionPrestamos({ noprestInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { redondeo } = useConfigCuotas();

  // ---- Config (provincias, formas de pago, marca/modelo) ----
  const [provincias, setProvincias] = useState([]);
  const [formaspago, setFormaspago] = useState(["Meses", "Quincenas", "Semanas", "Dias"]);
  const [marcamodelo, setMarcamodelo] = useState([]);
  useEffect(() => {
    let activo = true;
    post("/api/ares2040_config", {}).then(({ ok, data }) => {
      if (!activo || !ok) return;
      if (Array.isArray(data.provincias)) setProvincias(data.provincias);
      if (Array.isArray(data.formaspago) && data.formaspago.length) setFormaspago(data.formaspago);
      if (Array.isArray(data.marcamodelo)) setMarcamodelo(data.marcamodelo);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const marcas = useMemo(
    () => [...new Set(marcamodelo.map((m) => m.marca).filter(Boolean))],
    [marcamodelo]
  );

  // ---- Préstamos (todos, filtro local) ----
  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [prestamoSel, setPrestamoSel] = useState(null);

  const cargarPrestamos = () => {
    setBuscando(true);
    return post("/api/ares2040_prestamos", {})
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.prestamos)) setOpts(data.prestamos);
      })
      .finally(() => setBuscando(false));
  };

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Datos del préstamo ----
  const [form, setForm] = useState(FORM_INICIAL);
  // Tipo de documento (cédula / RNC / pasaporte): derivado del valor guardado.
  const [tipodoc, setTipodoc] = useState("cedula");
  const [tipodocFiador, setTipodocFiador] = useState("cedula");
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const [actual, setActual] = useState(null); // condiciones actuales (solo lectura)
  const [serializado, setSerializado] = useState(true);
  const [tienepagos, setTienepagos] = useState(false);
  const [cargando, setCargando] = useState(false);

  const modelosDeMarca = useMemo(
    () => marcamodelo.filter((m) => m.marca === form.marca).map((m) => m.modelo),
    [marcamodelo, form.marca]
  );

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !opts.length) return;
    const opcion = opts.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) { setPrestamoSel(opcion); cargarDatos(opcion.noprest); };
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  const cargarDatos = async (noprest) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/ares2040_datos", { noprest: String(noprest) });
      if (!ok || !data.prestamo) {
        showAlert({ message: data.Error || "No se pudo cargar el préstamo", color: "error" });
        return;
      }
      const p = data.prestamo;
      setForm({
        cedula: formatDocumentoAuto(p.cedula || ""),
        nombres: p.nombres || "",
        apellidos: p.apellidos || "",
        direccion: p.direccion || "",
        provincia: p.urb || "",
        telefono: p.telefono || "",
        celular: p.celular || "",
        email: p.email || "",
        apodo: p.apodo || "",
        edad: p.edad || "",
        chasis: p.chasis || "",
        marca: p.marca || "",
        modelo: p.modelo || "",
        colore: p.colore || "",
        garantia: p.garantia || "",
        comentario: p.comentario || "",
        fiador: p.fiador || "",
        fiadorced: p.fiadorced ? formatDocumentoAuto(p.fiadorced) : "",
        fiadortel: p.fiadortel || "",
        fiadordir: p.fiadordir || "",
      });
      setTipodoc(tipoDocumento(p.cedula));
      setTipodocFiador(tipoDocumento(p.fiadorced));
      setActual(p);
      setSerializado((p.serializado || "S").trim().toUpperCase() === "S");
      setTienepagos(!!p.tienepagos);
      setRecalcular(false);
      setNuevo(NUEVO_INICIAL);
      setReprog({ ...REPROG_INICIAL, fecha: p.proximacuota || "" });
      setPlanComp(propuestaPlanCompletivo(p.completivo));
    } finally {
      setCargando(false);
    }
  };

  // ---- Recálculo de condiciones ----
  const [recalcular, setRecalcular] = useState(false);
  const [nuevo, setNuevo] = useState(NUEVO_INICIAL);
  const setN = (k) => (e) => setNuevo((p) => ({ ...p, [k]: e.target.value }));

  // Al activar el recálculo se prellenan las condiciones vigentes: así se
  // puede reprogramar solo la fecha de la cuota dejando lo demás igual.
  // prestamo.interes es fracción por período; se convierte a % mensual, que
  // es lo que espera el formulario (y _calcular en el backend).
  const activarRecalculo = (on) => {
    setRecalcular(on);
    if (!on || !actual) return;
    setNuevo((p) => {
      if (p.precio || p.cuotas || p.interes) return p; // ya digitado, no pisar
      const periodos = String(actual.periodos || "").trim();
      const factor = FACTOR_PERIODO[periodos];
      // 8 decimales: con redondeo de cuotas la tasa guardada es la efectiva
      // y cortarla a 4 puede subir la cuota al siguiente múltiplo.
      const interesMensual = factor
        ? Math.round(toNum(actual.interes) * factor * 100 * 1e8) / 1e8
        : 0;
      return {
        ...p,
        precio: toNum(actual.precio) ? String(toNum(actual.precio)) : "",
        cuotas: actual.cuotas ? String(actual.cuotas) : "",
        formadepago: factor ? periodos : p.formadepago,
        interes: interesMensual ? String(interesMensual) : "",
        fechaprimeracuota: p.fechaprimeracuota || actual.primeracuota || "",
      };
    });
  };

  // Misma fórmula del escritorio (calculointeres) y de ares2010 (_calcular):
  // interés mensual % convertido a fracción por período, interés simple.
  const calculo = useMemo(() => {
    const financia = toNum(nuevo.precio) - toNum(actual?.inicial);
    const plazo = toNum(nuevo.cuotas);
    const r = calcularCuotas(
      financia,
      plazo,
      nuevo.formadepago || "Meses",
      toNum(nuevo.interes),
      redondeo
    );
    return r ? { financia, ...r } : null;
  }, [nuevo, actual, redondeo]);

  // Ventas generales / matrículas financiadas en las cuotas: el backend las
  // reparte de nuevo al recalcular (misma regla que _cuotas_para_cargo): si
  // iban en todas las cuotas van en todas las nuevas; si no, en las N primeras.
  const cargosRepartidos = useMemo(() => {
    const lista = actual?.cargosdistribuidos || [];
    const plazoNuevo = toNum(nuevo.cuotas);
    const plazoAnterior = toNum(actual?.cuotas);
    const cuotasNuevas = (c) => {
      if (!(plazoNuevo > 0)) return 0;
      if (plazoAnterior && c.cuotas >= plazoAnterior) return plazoNuevo;
      return Math.max(1, Math.min(c.cuotas, plazoNuevo));
    };
    return lista.map((c) => ({ ...c, cuotasnuevas: cuotasNuevas(c) }));
  }, [actual, nuevo.cuotas]);
  const totalCargos = cargosRepartidos.reduce((s, c) => s + toNum(c.valor), 0);

  // ---- Reprogramar cuotas pendientes (con pagos) ----
  const [reprog, setReprog] = useState(REPROG_INICIAL);
  const setR = (k) => (e) => setReprog((p) => ({ ...p, [k]: e.target.value }));
  const [reprogConfirm, setReprogConfirm] = useState(false);
  const [reprogramando, setReprogramando] = useState(false);

  const validarReprog = () => {
    if (!reprog.fecha) {
      showAlert({ message: "Indique la fecha de la próxima cuota", color: "warning" });
      return false;
    }
    if (actual?.periodos === "Quincenas" && (reprog.diaquincena1 || reprog.diaquincena2)) {
      const d1 = parseInt(reprog.diaquincena1, 10);
      const d2 = parseInt(reprog.diaquincena2, 10);
      if (!(d1 >= 1 && d1 <= 31) || !(d2 >= 1 && d2 <= 31) || d1 === d2) {
        showAlert({
          message:
            "Días fijos de quincena: indique los dos días, entre 1 y 31 y distintos (ej. 15 y 30), o déjelos vacíos para cuotas cada 15 días",
          color: "warning",
        });
        return false;
      }
    }
    return true;
  };

  const reprogramar = async () => {
    setReprogConfirm(false);
    setReprogramando(true);
    try {
      const noprest = prestamoSel?.noprest || prestamoSel;
      const { ok, data } = await post("/api/ares2040_reprogramar", {
        noprest: String(noprest),
        fechaprimeracuota: reprog.fecha,
        excluirdomingos: reprog.excluirdomingos,
        diaquincena1: reprog.diaquincena1,
        diaquincena2: reprog.diaquincena2,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Cuotas reprogramadas", color: "success" });
        cargarDatos(noprest);
      } else {
        showAlert({ message: data.Error || "Error al reprogramar", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setReprogramando(false);
    }
  };

  // ---- Plan del completivo del inicial ----
  const [planComp, setPlanComp] = useState([]);
  const [planCompConfirm, setPlanCompConfirm] = useState(false);
  const [guardandoPlan, setGuardandoPlan] = useState(false);
  const completivoPend = toNum(actual?.completivo?.pendiente);
  const estadoCompletivo = useMemo(
    () =>
      actual?.completivo?.plan?.length
        ? estadoPlanCompletivo(actual.completivo.plan, completivoPend)
        : null,
    [actual, completivoPend]
  );

  const validarPlanComp = () => {
    if (!planComp.length) {
      showAlert({ message: "Indique al menos un pago del plan", color: "warning" });
      return false;
    }
    const err = validarPlanCompletivo(planComp, completivoPend);
    if (err) {
      showAlert({ message: err, color: "warning" });
      return false;
    }
    return true;
  };

  const guardarPlanComp = async () => {
    setPlanCompConfirm(false);
    setGuardandoPlan(true);
    try {
      const noprest = prestamoSel?.noprest || prestamoSel;
      const { ok, data } = await post("/api/ares2040_completivoplan", {
        noprest: String(noprest),
        plan: normalizarPlan(planComp),
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Plan del completivo guardado", color: "success" });
        cargarDatos(noprest);
      } else {
        showAlert({ message: data.Error || "Error al guardar el plan", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardandoPlan(false);
    }
  };

  // ---- Guardar ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const limpiar = () => {
    setForm(FORM_INICIAL);
    setTipodoc("cedula");
    setTipodocFiador("cedula");
    setActual(null);
    setPrestamoSel(null);
    setSerializado(true);
    setTienepagos(false);
    setRecalcular(false);
    setNuevo(NUEVO_INICIAL);
    setReprog(REPROG_INICIAL);
    setPlanComp([]);
    cargarPrestamos();
  };

  const validar = () => {
    if (!actual) {
      showAlert({ message: "Seleccione un préstamo", color: "warning" });
      return false;
    }
    if (!documentoValido(form.cedula, tipodoc)) {
      showAlert({
        message: "El documento del cliente está en blanco o incompleto",
        color: "warning",
      });
      return false;
    }
    if (!form.nombres.trim() || !form.apellidos.trim()) {
      showAlert({ message: "Indique nombres y apellidos", color: "warning" });
      return false;
    }
    if (!form.direccion.trim()) {
      showAlert({ message: "La dirección no puede estar en blanco", color: "warning" });
      return false;
    }
    if (serializado && (!form.marca || !form.modelo)) {
      showAlert({ message: "Indique marca y modelo", color: "warning" });
      return false;
    }
    if (recalcular) {
      if (toNum(nuevo.precio) <= toNum(actual?.inicial)) {
        showAlert({
          message: "El nuevo precio no puede ser menor o igual al inicial",
          color: "warning",
        });
        return false;
      }
      if (toNum(nuevo.cuotas) <= 0 || toNum(nuevo.interes) <= 0) {
        showAlert({ message: "Complete cuotas e interés del recálculo", color: "warning" });
        return false;
      }
      // Días fijos de quincena: o ninguno (cada 15 días corridos) o los dos,
      // entre 1 y 31 y distintos — misma regla que la venta a crédito.
      if (nuevo.formadepago === "Quincenas" && (nuevo.diaquincena1 || nuevo.diaquincena2)) {
        const d1 = parseInt(nuevo.diaquincena1, 10);
        const d2 = parseInt(nuevo.diaquincena2, 10);
        if (!(d1 >= 1 && d1 <= 31) || !(d2 >= 1 && d2 <= 31) || d1 === d2) {
          showAlert({
            message:
              "Días fijos de quincena: indique los dos días, entre 1 y 31 y distintos (ej. 15 y 30), o déjelos vacíos para cuotas cada 15 días",
            color: "warning",
          });
          return false;
        }
      }
    }
    return true;
  };

  const guardar = async () => {
    setConfirmOpen(false);
    setGuardando(true);
    try {
      const noprest = actual ? prestamoSel?.noprest || prestamoSel : "";
      const { ok, data } = await post("/api/ares2040_guardar", {
        noprest: String(noprest),
        ...form,
        recalcular,
        precio: nuevo.precio,
        plazo: nuevo.cuotas,
        formadepago: nuevo.formadepago,
        interes: nuevo.interes,
        excluirdomingos: nuevo.excluirdomingos,
        diaquincena1: nuevo.diaquincena1,
        diaquincena2: nuevo.diaquincena2,
        fechaprimeracuota: nuevo.fechaprimeracuota,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Datos guardados correctamente", color: "success" });
        limpiar();
      } else {
        showAlert({ message: data.Error || "Error al guardar", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Modificación de Préstamos"
        subtitle="Modifica los datos y condiciones de un préstamo activo"
        cta={
          <Stack direction="row" spacing={1.2}>
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => validar() && setConfirmOpen(true)}
              disabled={guardando || !actual}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Buscador */}
        <Box sx={sectionBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={opts}
                loading={buscando || cargando}
                value={prestamoSel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => {
                  setPrestamoSel(v);
                  if (v?.noprest) cargarDatos(v.noprest);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.nombres, o.cedula].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="No. de préstamo, cliente o cédula" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <Stack direction="row" spacing={1}>
                {actual && !serializado && (
                  <Chip label="No serializado (garantía)" sx={{ fontWeight: 700 }} />
                )}
                {actual && tienepagos && (
                  <Chip
                    label="Con pagos registrados — no se puede recalcular"
                    sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
                  />
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {actual && (
          <>
            {/* Cliente */}
            <Box sx={sectionBox}>
              <SectionTitle icon={PersonOutlineIcon} color={FP.color.info}>
                Datos Generales
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={3}>
                  <DocumentoField
                    value={form.cedula}
                    tipo={tipodoc}
                    onChangeValor={(v) => setForm((p) => ({ ...p, cedula: v }))}
                    onChangeTipo={setTipodoc}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Nombres"
                    value={form.nombres}
                    onChange={setF("nombres")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Apellidos"
                    value={form.apellidos}
                    onChange={setF("apellidos")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Apodo"
                    value={form.apodo}
                    onChange={setF("apodo")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8} md={6}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={form.direccion}
                    onChange={setF("direccion")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <Autocomplete
                    options={provincias}
                    value={form.provincia || null}
                    onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Provincia" sx={fpInputSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={form.telefono}
                    onChange={setF("telefono")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Celular"
                    value={form.celular}
                    onChange={setF("celular")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={form.email}
                    onChange={setF("email")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Edad"
                    value={form.edad}
                    onChange={setF("edad")}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Producto o Garantía */}
            <Box sx={sectionBox}>
              <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
                {serializado ? "Producto / Vehículo" : "Garantía"}
              </SectionTitle>
              {serializado ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Chasis"
                      value={form.chasis}
                      InputProps={{ readOnly: true }}
                      sx={fpInputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Marca"
                      value={form.marca}
                      sx={fpInputSx}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, marca: e.target.value, modelo: "" }))
                      }
                    >
                      {marcas.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Modelo"
                      value={form.modelo}
                      onChange={setF("modelo")}
                      sx={fpInputSx}
                    >
                      {modelosDeMarca.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Color"
                      value={form.colore}
                      onChange={setF("colore")}
                      sx={fpInputSx}
                    />
                  </Grid>
                </Grid>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Descripción de la garantía"
                  value={form.garantia}
                  onChange={setF("garantia")}
                  sx={fpInputSx}
                />
              )}
            </Box>

            {/* Condiciones actuales */}
            <Box sx={sectionBox}>
              <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.navy}>
                Condiciones Actuales
              </SectionTitle>
              <Grid container spacing={2}>
                {[
                  ["Precio", fmtMoney(actual.precio)],
                  ["Inicial", fmtMoney(actual.inicial)],
                  ["Financiado", fmtMoney(actual.financia)],
                  ["Cuotas", `${actual.cuotas ?? ""} ${actual.periodos || ""}`],
                  ["Valor cuota", fmtMoney(actual.vcuotas)],
                  ["Deuda total", fmtMoney(actual.cantsol)],
                  ["Interés (fracción/período)", actual.interes],
                  ["Abono a capital", fmtMoney(actual.abonoacapital)],
                ].map(([k, v]) => (
                  <Grid item xs={6} sm={3} key={k}>
                    <TextField
                      fullWidth
                      label={k}
                      value={String(v ?? "")}
                      InputProps={{ readOnly: true }}
                      sx={fpInputSx}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Recálculo de condiciones */}
            {!tienepagos && (
              <Box sx={sectionBox}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <SectionTitle icon={CalculateOutlinedIcon} color={FP.color.success}>
                    Recalcular Condiciones
                  </SectionTitle>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={recalcular}
                        onChange={(e) => activarRecalculo(e.target.checked)}
                      />
                    }
                    label="Activar"
                  />
                </Stack>
                {recalcular && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Nuevo precio"
                        value={nuevo.precio}
                        onChange={setN("precio")}
                        sx={fpInputSx}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Cuotas"
                        value={nuevo.cuotas}
                        onChange={setN("cuotas")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <TextField
                        select
                        fullWidth
                        label="Período"
                        value={nuevo.formadepago}
                        onChange={setN("formadepago")}
                        sx={fpInputSx}
                      >
                        {formaspago.map((f) => (
                          <MenuItem key={f} value={f}>
                            {f}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {nuevo.formadepago === "Dias" && (
                      <Grid item xs={12} sm={6} md={4.8} sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={nuevo.excluirdomingos}
                              onChange={(e) =>
                                setNuevo((p) => ({ ...p, excluirdomingos: e.target.checked }))
                              }
                            />
                          }
                          label="Excluir domingos (las cuotas saltan al lunes)"
                        />
                      </Grid>
                    )}
                    {nuevo.formadepago === "Quincenas" && (
                      <>
                        <Grid item xs={6} sm={6} md={2.4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Día fijo 1"
                            value={nuevo.diaquincena1}
                            onChange={setN("diaquincena1")}
                            sx={fpInputSx}
                            inputProps={{ min: 1, max: 31 }}
                            helperText="Ej: 15. Vacíos = cada 15 días"
                          />
                        </Grid>
                        <Grid item xs={6} sm={6} md={2.4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Día fijo 2"
                            value={nuevo.diaquincena2}
                            onChange={setN("diaquincena2")}
                            sx={fpInputSx}
                            inputProps={{ min: 1, max: 31 }}
                            helperText="Ej: 30 (en febrero cae al 28/29)"
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={6} md={2.4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Interés % mensual"
                        value={nuevo.interes}
                        onChange={setN("interes")}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Primera cuota (reprogramar)"
                        value={nuevo.fechaprimeracuota}
                        onChange={setN("fechaprimeracuota")}
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                        helperText="Viene con la primera cuota actual; cámbiela para reprogramar"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2.4}>
                      <CampoCuota
                        editable={redondeo}
                        label="Valor cuota"
                        cuota={calculo ? calculo.valorcuota : null}
                        valorMostrado={calculo ? fmtMoney(calculo.valorcuota) : ""}
                        financia={toNum(nuevo.precio) - toNum(actual?.inicial)}
                        plazo={toNum(nuevo.cuotas)}
                        formadepago={nuevo.formadepago || "Meses"}
                        onInteres={(interes) => setNuevo((p) => ({ ...p, interes }))}
                        sx={fpInputSx}
                      />
                    </Grid>
                    {calculo && (
                      <Grid item xs={12}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: FP.color.textSecondary,
                            fontSize: "0.85rem",
                          }}
                        >
                          Financiado: RD$ {fmtMoney(calculo.financia)} · Deuda total: RD${" "}
                          {fmtMoney(calculo.deudatotal)}
                        </Typography>
                      </Grid>
                    )}
                    {cargosRepartidos.length > 0 && (
                      <Grid item xs={12}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.85rem" }}>
                          <b>Cargos financiados en las cuotas</b> (se reparten de nuevo, sin
                          interés): {cargosRepartidos.map(textoCargo).join(" · ")}
                          {calculo && (
                            <>
                              {" · "}
                              <b>
                                Deuda total con cargos: RD${" "}
                                {fmtMoney(calculo.deudatotal + totalCargos)}
                              </b>
                            </>
                          )}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            )}

            {/* Reprogramar cuotas pendientes (solo con pagos) */}
            {tienepagos && (
              <Box sx={sectionBox}>
                <SectionTitle icon={EventRepeatOutlinedIcon} color={FP.color.success}>
                  Reprogramar Cuotas Pendientes
                </SectionTitle>
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, mb: 2 }}>
                  {`Recorre las fechas de las ${actual.cuotaspendientes ?? 0} cuotas pendientes sin
                  pago a partir de la fecha indicada, con el período del préstamo
                  (${String(actual.periodos || "").trim() || "—"}). Las condiciones, los montos y
                  las cuotas pagadas o con abonos no se modifican.`}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Próxima cuota"
                      value={reprog.fecha}
                      onChange={setR("fecha")}
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                      helperText="Viene con la próxima cuota pendiente actual"
                    />
                  </Grid>
                  {String(actual.periodos || "").trim() === "Dias" && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={reprog.excluirdomingos}
                            onChange={(e) =>
                              setReprog((p) => ({ ...p, excluirdomingos: e.target.checked }))
                            }
                          />
                        }
                        label="Excluir domingos (las cuotas saltan al lunes)"
                      />
                    </Grid>
                  )}
                  {String(actual.periodos || "").trim() === "Quincenas" && (
                    <>
                      <Grid item xs={6} sm={3} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Día fijo 1"
                          value={reprog.diaquincena1}
                          onChange={setR("diaquincena1")}
                          sx={fpInputSx}
                          inputProps={{ min: 1, max: 31 }}
                          helperText="Ej: 15. Vacíos = cada 15 días"
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Día fijo 2"
                          value={reprog.diaquincena2}
                          onChange={setR("diaquincena2")}
                          sx={fpInputSx}
                          inputProps={{ min: 1, max: 31 }}
                          helperText="Ej: 30 (en febrero cae al 28/29)"
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      startIcon={
                        reprogramando ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <EventRepeatOutlinedIcon />
                        )
                      }
                      onClick={() => validarReprog() && setReprogConfirm(true)}
                      disabled={reprogramando || !(actual.cuotaspendientes > 0)}
                      sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                    >
                      Reprogramar
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Plan del completivo del inicial (modificar o crear) */}
            {completivoPend > 0 && (
              <Box sx={sectionBox}>
                <SectionTitle icon={EventNoteOutlinedIcon} color={FP.color.accentBlue}>
                  Plan del Inicial (Completivo)
                </SectionTitle>
                <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, mb: 2 }}>
                  {`Completivo del inicial pendiente: RD$ ${fmtMoney(completivoPend)}. ${
                    estadoCompletivo
                      ? "El plan nuevo reparte solo lo pendiente; lo ya cobrado se conserva como pagado."
                      : "La venta no tiene plan de fechas: todo el pendiente vence de una vez. Cree un plan para repartirlo."
                  } Las fechas no pueden ser anteriores a hoy.`}
                </Typography>
                <Grid container spacing={2.4}>
                  {estadoCompletivo && (
                    <Grid item xs={12} md={5}>
                      <Typography
                        sx={{ fontWeight: 800, fontSize: "0.82rem", color: FP.color.textPrimary }}
                      >
                        Plan actual
                      </Typography>
                      <PlanCompletivoResumen estado={estadoCompletivo} />
                    </Grid>
                  )}
                  <Grid item xs={12} md={estadoCompletivo ? 7 : 12}>
                    <PlanCompletivoEditor
                      completivo={completivoPend}
                      plan={planComp}
                      onChange={setPlanComp}
                    />
                    <Stack direction="row" spacing={1.2} justifyContent="flex-end" sx={{ mt: 1.6 }}>
                      <Button
                        onClick={() => setPlanComp(propuestaPlanCompletivo(actual.completivo))}
                        disabled={guardandoPlan}
                        sx={btnGhost}
                      >
                        Deshacer cambios
                      </Button>
                      <Button
                        startIcon={
                          guardandoPlan ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        onClick={() => validarPlanComp() && setPlanCompConfirm(true)}
                        disabled={guardandoPlan}
                        sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                      >
                        {estadoCompletivo ? "Guardar plan" : "Crear plan"}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Fiador y comentario */}
            <Box sx={{ ...sectionBox, mb: 0 }}>
              <SectionTitle icon={GroupOutlinedIcon} color={FP.color.info}>
                Fiador y Comentario
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fiador"
                    value={form.fiador}
                    onChange={setF("fiador")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DocumentoField
                    label="Documento fiador"
                    value={form.fiadorced}
                    tipo={tipodocFiador}
                    onChangeValor={(v) => setForm((p) => ({ ...p, fiadorced: v }))}
                    onChangeTipo={setTipodocFiador}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Teléfono fiador"
                    value={form.fiadortel}
                    onChange={setF("fiadortel")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Dirección fiador"
                    value={form.fiadordir}
                    onChange={setF("fiadordir")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Comentario"
                    value={form.comentario}
                    onChange={setF("comentario")}
                    inputProps={{ maxLength: 1000 }}
                    sx={fpInputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>

      {/* Confirmación */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Guardar modificación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {recalcular
              ? "Se guardarán los datos y se REGENERARÁ la tabla de amortización con las nuevas condiciones. ¿Desea continuar?"
              : "Se guardarán los datos generales del préstamo. ¿Desea continuar?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={btnGhost}>
            No
          </Button>
          <Button onClick={guardar} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Sí, guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación del plan del completivo */}
      <Dialog
        open={planCompConfirm}
        onClose={() => setPlanCompConfirm(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Plan del completivo del inicial</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`El completivo pendiente de RD$ ${fmtMoney(completivoPend)} quedará en ${
              planComp.length === 1 ? "1 pago" : `${planComp.length} pagos`
            }. El cobro propondrá lo que venza según las fechas nuevas. ¿Desea continuar?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPlanCompConfirm(false)} sx={btnGhost}>
            No
          </Button>
          <Button onClick={guardarPlanComp} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Sí, guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación de reprogramación de cuotas pendientes */}
      <Dialog
        open={reprogConfirm}
        onClose={() => setReprogConfirm(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reprogramar cuotas pendientes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Se recorrerán las fechas de ${actual?.cuotaspendientes ?? 0} cuotas pendientes sin
            pago a partir del ${reprog.fecha}. Las condiciones y los montos no cambian. ¿Desea
            continuar?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setReprogConfirm(false)} sx={btnGhost}>
            No
          </Button>
          <Button onClick={reprogramar} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Sí, reprogramar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
